import { cn } from '@sohanemon/utils';
import { m } from '@sohanscript/i18n';
import { Safari } from '@sohanscript/ui/mock/safari';
import { ClientOnly } from '@tanstack/react-router';
import { Hydrate } from '@tanstack/react-start';
import { idle } from '@tanstack/react-start/hydration';
import React from 'react';

export const Embed = ({
	src,
	title,
	className,
	element: selector,
	styles,
}: {
	src?: string;
	title?: string;
	className?: string;
	element?: string;
	styles?: string;
}) => {
	const isolated = React.useRef(false);

	if (!src) return null;
	return (
		<ClientOnly>
			<Hydrate when={idle()}>
				<Safari
					url={`file:///sohan${src}.tsx`}
					className={cn('not-prose', className)}
				>
					<iframe
						onLoad={(e) => {
							const iframe = e.currentTarget;
							const doc = iframe.contentDocument;
							if (!doc?.body) return;

							iframe.style.opacity = '0';

							const isolateLiveElement = () => {
								if (isolated.current) return;
								isolated.current = true;

								observer.disconnect();

								if (selector) {
									const target = doc.querySelector(selector) as HTMLElement;

									if (target) {
										// 1. Give it a distinct marker class so we know it's our chosen target
										target.classList.add('isolated-live-target');

										// 2. Inject structural CSS *before* moving it.
										// This hides every other element at the body root level,
										// but allows our live target to render normally.
										if (!doc.getElementById('isolation-override-styles')) {
											const styleTag = doc.createElement('style');
											styleTag.id = 'isolation-override-styles';
											styleTag.textContent = `
												body > :not(.isolated-live-target):not(style):not(script) {
													display: none !important;
												}
												body {
													padding: 0 !important;
													margin: 0 !important;
													background-image: none !important;
												}
											`;
											if (styles) {
												styleTag.textContent += `\n${styles}`;
											}
											doc.head.appendChild(styleTag);
										}

										// 3. Move the LIVE node to the body root if it isn't already there.
										// moving it via appendChild preserves its JS events and Framer Motion state.
										if (target.parentElement !== doc.body) {
											doc.body.appendChild(target);
										}

										iframe.style.opacity = '1';
									}
								} else {
									// Fallback: If no selector, keep normal rendering
									iframe.style.opacity = '1';
								}

								observer.observe(doc.body, {
									childList: true,
									subtree: true,
								});
							};

							const observer = new MutationObserver(() => {
								isolateLiveElement();
							});

							observer.observe(doc.body, {
								childList: true,
								subtree: true,
							});

							isolateLiveElement();
						}}
						src={src}
						title={title ?? m.embed_demo_title()}
						className="w-full flex-1 transition-opacity duration-200"
						loading="lazy"
						sandbox="allow-scripts allow-same-origin"
					/>
				</Safari>
			</Hydrate>
		</ClientOnly>
	);
};
